import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useAuth } from './useAuth';

export default function PrivateRoute() {
  const { authState, oktaAuth } = useAuth();
  
  const [isReady, setIsReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (!authState) {
      return undefined;
    }

    // Handle authentication state (works for both debug and Okta modes)
    if (!oktaAuth) {
      return undefined;
    }

    if (authState.error) {
      if (isMounted) {
        setErrorMessage(authState.error.message);
      }
      return undefined;
    }

    if (!authState.isAuthenticated) {
      if (isMounted) {
        setIsReady(false);
      }
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri?.(originalUri);
      oktaAuth.signInWithRedirect?.().catch(error => {
        if (isMounted) {
          setErrorMessage(error instanceof Error ? error.message : 'Unable to start Okta login.');
        }
      });
      return () => {
        isMounted = false;
      };
    }

    if (isMounted) {
      setErrorMessage(null);
      setIsReady(true);
    }

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  if (errorMessage) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white text-center text-sm text-red-600">
        <p className="font-semibold">Authentication error</p>
        <p className="mt-2 max-w-sm text-xs text-red-500">{errorMessage}</p>
      </div>
    );
  }

  if (!authState || !isReady || (authState.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-gray-600">
        <span>Checking authentication…</span>
      </div>
    );
  }

  return <Outlet />;
}
